import {
  Axis,
  AxisTickStrategies,
  ChartXY,
  ColorCSS,
  Dashboard,
  FontSettings,
  LegendOrientation,
  SolidFill,
  SolidLine,
  emptyFill,
  Themes,
  UIElementBuilders,
  UILayoutBuilders,
  UIOrigins,
  type DashboardCellOptions,
  type Legend,
} from '@lightningchart/lcjs';

export interface Value {
    timestamp: string;
    flow: number;
    pressure: number;
    volume: number;
    cement: number;
}

interface SeriesOptions<T> {
  name: string;
  color?: string;
  yAxis?: any;
  visible?: boolean;
  showPoints?: boolean;
  formatter?: (value: number) => string;
  map?: (data: T[]) => { x: number; y: number }[];
}

export interface SplineSeriesOptions<T> extends SeriesOptions<T> { }

export interface InjectionsChartOptions {
  chartTitle: string;
  container?: string | HTMLDivElement;
  dashboard?: Dashboard;
  dashboardCellOptions?: DashboardCellOptions;
}

interface InternalSeriesTracker<T> {
  series: any;
    yAxis?: {
    axis: Axis;
    opposite: boolean;
  };
  formatter?: (value: number) => string;
  map?: (data: T[]) => { x: number; y: number }[];
  add: (data: T[]) => void;
}


export class SessionChart<T extends Value> {
  chart: ChartXY;
  leftYAxis: Axis;
  rightYAxis: Axis;
  leftseries: InternalSeriesTracker<T>[] = [];
  rightseries: InternalSeriesTracker<T>[] = [];
  legends: Legend[] = [];

  get xAxis() {
    return this.chart.getDefaultAxisX();
  }

  get series() {
    return [...this.leftseries, ...this.rightseries];
  }

  constructor(opts: InjectionsChartOptions) {
    this.chart =
        opts.dashboard?.createChartXY(opts.dashboardCellOptions!) ??
        useUseLightningCharts().ChartXY({ defaultAxisX: { type: 'linear-highPrecision' }, container: opts.container, theme: Themes.light });
        this.chart.setTitle(opts.chartTitle).setCursorMode(undefined).setPadding({ bottom: 40 });
        this.leftYAxis = this.chart
            .getDefaultAxisY()
            .setTitle('Flow & Pressure');
        this.rightYAxis = this.chart
            .addAxisY({ opposite: true })
            .setTitle('Volume & Cement');

    // Use Numeric strategy to prevent auto-expansion (calendar rounding).
    // Format the numeric ticks (epoch ms) back to locale string for display.
    this.xAxis.setTickStrategy(AxisTickStrategies.Numeric, (tickStrategy) =>
      tickStrategy.setFormattingFunction((value) => new Date(value).toLocaleString('no-NO')));

    this.leftseries = [];
    this.rightseries = [];
    this.legends = [];
  }

  setTitle(title: string) {
    this.chart.setTitle(title);
    return this;
  }

  add(values: T[]) {
    const sortedValues = [...values].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    this.series.forEach(s => s.add(sortedValues));

    this.xAxis.setScrollStrategy(undefined);

    if (sortedValues.length > 0) {
      const min = new Date(sortedValues[0].timestamp).getTime();
      const max = new Date(sortedValues[sortedValues.length - 1].timestamp).getTime();

      if (min < max) {
        this.xAxis.setInterval({ start: min, end: max, animate: false });
      } else {
        this.xAxis.setInterval({ start: min - 60000, end: max + 60000, animate: false });
      }
    }
    const leftMax = Math.max(1, ...this.leftseries
      .flatMap(s => s.map ? s.map(sortedValues).map(p => p.y) : [])
      .filter(y => isFinite(y) && y >= 0));
    this.leftYAxis.setInterval({ start: 0, end: leftMax * 1.1, animate: false });

    const rightMax = Math.max(1, ...this.rightseries
      .flatMap(s => s.map ? s.map(sortedValues).map(p => p.y) : [])
      .filter(y => isFinite(y) && y >= 0));
    this.rightYAxis.setInterval({ start: 0, end: rightMax * 1.1, animate: false });

    // Prevent zooming out beyond the data range on all axes
    this.xAxis.setIntervalRestrictions((state) => ({
      startMin: state.dataMin,
      endMax: state.dataMax,
    }));
    this.leftYAxis.setIntervalRestrictions((state) => ({
      startMin: 0,
      endMax: state.dataMax,
    }));
    this.rightYAxis.setIntervalRestrictions((state) => ({
      startMin: 0,
      endMax: state.dataMax,
    }));
    return this;
  }

  clear() {
    this.series.forEach(s => s.series.clear());
    return this;
  }

  // fit() {
  //   this.xAxis.fit(true, false);
  //   this.leftYAxis.fit(true, false);
  //   this.rightYAxis.fit(true, false);
  //   return this;
  // }

  private __addSeries(series: InternalSeriesTracker<T>, opts: SplineSeriesOptions<T>) {
    if (opts.yAxis?.opposite) {
      this.rightseries.push(series);
    } else {
      this.leftseries.push(series);
    }
  }

  addSplineSeries(opts: SplineSeriesOptions<T>) {
    const series = this.chart
      .addSplineSeries({
          yAxis: opts.yAxis?.axis,
          schema: { x: { pattern: 'progressive' }, y: { pattern: null } },
      })
      .setName(opts.name)
      .setVisible(opts.visible ?? true);
    if (opts.color) {
      series.setStrokeStyle(new SolidLine({ thickness: 2, fillStyle: new SolidFill({ color: ColorCSS(opts.color) }) }));
    }
    series.setPointFillStyle(opts.showPoints && opts.color ? new SolidFill({ color: ColorCSS(opts.color) }) : emptyFill);

    this.__addSeries({
      series,
      yAxis: opts.yAxis,
      formatter: opts.formatter,
      map: opts.map,
      add: (data: any[]) => {
          const formattedData = opts.map ? opts.map(data) : data;
          series.appendJSON(formattedData);
      },
    }, opts);
    return this;
  }

  addLegendBox(series: InternalSeriesTracker<T>[], builder?: any, scale?: { x: number; y: number; }) {
    const legend = this.chart
      .addLegend({ orientation: LegendOrientation.Horizontal, position: scale ?? { x: 50, y: 0 } });
    series.forEach(s => legend.add(s.series));
    this.legends.push(legend);
    return this;
  }

  addCustomCursor(xAxis: Axis, leftYAxis: Axis, rightYAxis?: Axis) {
    // Create UI elements for custom cursor.
    const tooltip = this.chart
      .addUIElement(UILayoutBuilders.Column, { x: xAxis, y: leftYAxis })
      .setOrigin(UIOrigins.LeftBottom)
      .setMargin(5)
      .setBackground((background: any) =>
        background
          // Style same as Theme result table.
          .setFillStyle(this.chart.getTheme().cursorResultTableFillStyle)
          .setStrokeStyle(this.chart.getTheme().cursorResultTableStrokeStyle),
      );

    const header = tooltip
      .addElement(UILayoutBuilders.Row)
      .addElement(UIElementBuilders.TextBox)
      .setTextFont(new FontSettings({ size: 14, weight: 'bold' }));

    const body = tooltip.addElement(UILayoutBuilders.Row);

    const seriesNames = body.addElement(UILayoutBuilders.Column);

    const seriesValues = body.addElement(UILayoutBuilders.Column);

    const rowsY = this.series.map(s => {
      let fillStyle = s.series.getStrokeStyle().getFillStyle();
      return [
        seriesNames
          .addElement(UILayoutBuilders.Row)
          .addElement(UIElementBuilders.TextBox)
          .setTextFillStyle(fillStyle)
          .setTextFont(new FontSettings({ size: 14, weight: 'bold' })),
        seriesValues
          .addElement(UILayoutBuilders.Row)
          .addElement(UIElementBuilders.TextBox)
          .setMargin({ top: 1.6 })
          .setTextFillStyle(fillStyle)
          .setTextFont(new FontSettings({ size: 14, weight: 'bold' })),
      ];
    });

    const tickX = this.xAxis.addCustomTick(UIElementBuilders.PointableTextBox).setAllocatesAxisSpace(false);

    const leftVisibleTicksY = this.leftseries
      .filter(s => s.series.getVisible() === true)
      .map(s =>
        leftYAxis
          .addCustomTick(UIElementBuilders.PointableTextBox)
          .setAllocatesAxisSpace(false)
          .setMarker(marker => marker.setTextFillStyle(s.series.getStrokeStyle().getFillStyle())),
      );

    const rightVisibleTicksY = rightYAxis
      ? this.rightseries
        .filter(s => s.series.getVisible() === true)
        .map(s =>
          rightYAxis
            .addCustomTick(UIElementBuilders.PointableTextBox)
            .setAllocatesAxisSpace(false)
            .setMarker(marker => marker.setTextFillStyle(s.series.getStrokeStyle().getFillStyle())),
        )
      : [];

    const visibleTicksY = [...leftVisibleTicksY, ...rightVisibleTicksY];

    // Hide custom cursor components initially.
    tooltip.setVisible(false);
    tickX.setVisible(false);
    visibleTicksY.forEach(tick => tick.setVisible(false));

    const showCursorAt = (clientCoordinate: MouseEvent) => {
      // Translate mouse location to Axis.
      const mouseLocationAxis = this.chart.translateCoordinate(clientCoordinate, this.chart.coordsAxis);

      // Solve nearest data point to the mouse on each series.
      const solveResults: any[] = (this.chart as any).solveNearest?.(clientCoordinate, 'show-all') ?? [];
      const nearestDataPoints = this.series.map(s => ({
        series: s,
        cursor: solveResults.find((r: any) => r.series === s.series) ?? null,
      }));
      const visibleDataPoints = nearestDataPoints.filter(dp => dp.series.series.getVisible() === true);

      // Find the nearest solved data point to the mouse.
      const nearestPoint = nearestDataPoints.reduce((prev, curr) => {
        if (!prev.cursor) return curr;
        if (!curr.cursor) return prev;
        return Math.abs(mouseLocationAxis.y - curr.cursor.y) <
          Math.abs(mouseLocationAxis.y - prev.cursor.y)
          ? curr
          : prev;
      });

      if (nearestPoint.cursor) {
        const cx: number = nearestPoint.cursor.x;
        const cy: number = nearestPoint.cursor.y;

        // Set custom cursor location.
        tooltip.setPosition({ x: cx, y: cy });

        // Change origin of result table based on cursor location.
        if (cx > this.xAxis.getInterval().end / 1.5) {
          if (cy > leftYAxis.getInterval().end / 1.5) {
            tooltip.setOrigin(UIOrigins.RightTop);
          } else {
            tooltip.setOrigin(UIOrigins.RightBottom);
          }
        } else if (cy > leftYAxis.getInterval().end / 1.5) {
          tooltip.setOrigin(UIOrigins.LeftTop);
        } else {
          tooltip.setOrigin(UIOrigins.LeftBottom);
        }

        // Format result table text.
        header.setText(`Time: ${this.xAxis.formatValue(cx)}`);
        rowsY.map((rowY, i) => {
          const nearestSeriesPoint = nearestDataPoints[i];
          rowY[0].setText(nearestSeriesPoint.series.series.getName());
          if (nearestSeriesPoint.series.formatter && nearestSeriesPoint.cursor) {
             rowY[1].setText(nearestSeriesPoint.series.formatter(nearestSeriesPoint.cursor.y));
          } else {
             rowY[1].setText('');
          }
        });

        // Position custom ticks.
        tickX.setValue(cx);
        visibleTicksY.forEach((tick, i) => tick.setValue(visibleDataPoints[i]?.cursor?.y ?? visibleDataPoints[i]?.cursor?.location?.y ?? 0));

        // Display cursor.
        tooltip.setVisible(true);
        tickX.setVisible(true);
        visibleTicksY.map(t => t.setVisible(true));
      } else {
        // Hide cursor.
        tooltip.setVisible(false);
        tickX.setVisible(false);
        visibleTicksY.map(t => t.setVisible(false));
      }
    };

    // Implement custom cursor logic with events.
    const hideCursor = () => {
      tooltip.setVisible(false);
      tickX.setVisible(false);
      visibleTicksY.map(tick => tick.setVisible(false));
    };

    this.chart.seriesBackground.addEventListener('pointermove', (event: any) => showCursorAt(event));
    this.chart.seriesBackground.addEventListener('pointerleave', hideCursor);
    this.chart.seriesBackground.addEventListener('pointerdown', hideCursor);

    return this;
  }

  setMouseInteractions(enabled: boolean) {
    this.chart.setUserInteractions(enabled ? undefined : { pan: false, zoom: false, rectangleZoom: false, restoreDefault: false, restorePrevious: false } as any);
    const axisConfig = enabled ? undefined : { pan: false, zoom: false, rectangleZoom: false } as any;
    this.leftYAxis.setUserInteractions(axisConfig);
    this.rightYAxis.setUserInteractions(axisConfig);
    this.xAxis.setUserInteractions(axisConfig);
  }
}
